import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner } from 'react-bootstrap';
import axios from '../utils/api';
import './BuyerAddressManager.css';
import { useStoreSettings } from '../contexts/StoreSettingsContext';
import { COUNTRY_ISD_CODES, getPhoneRuleByIsd } from '../constants/countryIsdCodes';

const initialForm = {
  name: '',
  contact_isd_code: '+91',
  contact_number: '',
  addr_line1: '',
  addr_line2: '',
  city: '',
  state: '',
  country: 'India',
  pincode: '',
  is_default: false,
  is_billing: false,
};

const BuyerAddressManager = () => {
  const [addresses, setAddresses] = useState([]);
  const [addressFilter, setAddressFilter] = useState('all');
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
  });
  const { settings: storeSettings } = useStoreSettings();

  const stateOptions = useMemo(
    () => states.filter((item) => item.is_active).sort((left, right) => left.sort_order - right.sort_order || left.name.localeCompare(right.name)),
    [states]
  );
  const canSelectOtherIsd = Boolean(storeSettings.allow_non_india_user_isd);
  const phoneRule = getPhoneRuleByIsd(formData.contact_isd_code);
  const phoneLengthHint = phoneRule.minLength === phoneRule.maxLength
    ? `${phoneRule.minLength} digits`
    : `${phoneRule.minLength}-${phoneRule.maxLength} digits`;
  const filteredAddresses = useMemo(() => {
    if (addressFilter === 'billing') {
      return addresses.filter((address) => Boolean(address.is_billing));
    }
    if (addressFilter === 'default') {
      return addresses.filter((address) => Boolean(address.is_default));
    }
    return addresses;
  }, [addresses, addressFilter]);

  useEffect(() => {
    if (!canSelectOtherIsd && formData.contact_isd_code !== '+91') {
      setFormData((current) => ({ ...current, contact_isd_code: '+91' }));
    }
  }, [canSelectOtherIsd, formData.contact_isd_code]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');
        const [addressResponse, stateResponse] = await Promise.all([
          axios.get('/api/buyer-addresses'),
          axios.get('/api/india-states'),
        ]);
        const loadedAddresses = addressResponse.data || [];
        setAddresses(loadedAddresses);
        setStates(stateResponse.data || []);
      } catch (err) {
        setError('Failed to load addresses');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const resetForm = () => {
    setFormData({
      ...initialForm,
      is_default: addresses.length === 0,
      is_billing: addresses.length === 0,
    });
    setEditingAddress(null);
    setShowModal(false);
  };

  const openCreateModal = () => {
    setFormData({
      ...initialForm,
      is_default: addresses.length === 0,
      is_billing: addresses.length === 0,
    });
    setEditingAddress(null);
    setShowModal(true);
  };

  const openEditModal = (address) => {
    setFormData({
      name: address.name || '',
      contact_isd_code: address.contact_isd_code || '+91',
      contact_number: address.contact_number || '',
      addr_line1: address.addr_line1,
      addr_line2: address.addr_line2 || '',
      city: address.city,
      state: address.state,
      country: 'India',
      pincode: address.pincode.toString(),
      is_default: address.is_default,
      is_billing: Boolean(address.is_billing),
    });
    setEditingAddress(address);
    setShowModal(true);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (name === 'contact_number') {
      setFormData((current) => ({ ...current, contact_number: value.replace(/\D/g, '').slice(0, phoneRule.maxLength) }));
      return;
    }
    if (name === 'pincode') {
      setFormData((current) => ({ ...current, pincode: value.replace(/\D/g, '').slice(0, 6) }));
      return;
    }
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) return 'Name is required';
    if (formData.name.length > 80) return 'Name cannot exceed 80 characters';
    if (!formData.contact_number) return 'Contact number is required';
    if (formData.contact_number.length < phoneRule.minLength || formData.contact_number.length > phoneRule.maxLength) {
      return `Contact number for ${formData.contact_isd_code} must be ${phoneLengthHint}`;
    }
    if (!formData.addr_line1.trim()) return 'Address line 1 is required';
    if (formData.addr_line1.length > 100) return 'Address line 1 cannot exceed 100 characters';
    if (formData.addr_line2.length > 100) return 'Address line 2 cannot exceed 100 characters';
    if (!formData.city.trim()) return 'City is required';
    if (formData.city.length > 40) return 'City cannot exceed 40 characters';
    if (!formData.state) return 'State or UT is required';
    if (!/^\d{6}$/.test(formData.pincode)) return 'Pincode must be exactly 6 digits';
    return '';
  };

  const reloadAddresses = async (message = '') => {
    const response = await axios.get('/api/buyer-addresses');
    setAddresses(response.data || []);
    if (message) {
      setSuccess(message);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...formData,
        country: 'India',
        pincode: Number(formData.pincode),
      };
      if (editingAddress) {
        await axios.put(`/api/buyer-addresses/${editingAddress.id}`, payload);
        await reloadAddresses('Address updated successfully');
      } else {
        await axios.post('/api/buyer-addresses', payload);
        await reloadAddresses('Address added successfully');
      }
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  const openConfirmDialog = (title, message, confirmText, onConfirm) => {
    setConfirmDialog({
      show: true,
      title,
      message,
      confirmText,
      onConfirm,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ show: false, title: '', message: '', confirmText: 'Confirm', onConfirm: null });
  };

  const executeConfirmDialogAction = async () => {
    const action = confirmDialog.onConfirm;
    closeConfirmDialog();
    if (typeof action === 'function') {
      await action();
    }
  };

  const handleDelete = async (addressId) => {
    openConfirmDialog('Delete Address', 'Delete this address?', 'Delete', async () => {
      try {
        setError('');
        setSuccess('');
        await axios.delete(`/api/buyer-addresses/${addressId}`);
        await reloadAddresses('Address deleted successfully');
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to delete address');
      }
    });
  };

  const handleSetDefault = async (addressId) => {
    try {
      setError('');
      setSuccess('');
      await axios.put(`/api/buyer-addresses/${addressId}/default`);
      await reloadAddresses('Default address updated successfully');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update default address');
    }
  };

  const handleSetBilling = async (addressId) => {
    try {
      setError('');
      setSuccess('');
      await axios.put(`/api/buyer-addresses/${addressId}/billing`);
      await reloadAddresses('Billing address updated successfully');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update billing address');
    }
  };

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading addresses...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h1>Shipping Addresses</h1>
          <p className="text-muted mb-0">Maintain multiple delivery addresses and choose a default for checkout.</p>
        </Col>
        <Col xs="auto">
          <Button onClick={openCreateModal}>Add Address</Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {addresses.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <h5>No shipping addresses added yet</h5>
            <p className="text-muted">Add your first address to enable checkout.</p>
            <Button onClick={openCreateModal}>Add Address</Button>
          </Card.Body>
        </Card>
      ) : (
        <>
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <div className="text-muted small">
              Showing {filteredAddresses.length} of {addresses.length} addresses
            </div>
            <div className="btn-group" role="group" aria-label="Address filter">
              <Button
                size="sm"
                variant={addressFilter === 'all' ? 'dark' : 'outline-dark'}
                onClick={() => setAddressFilter('all')}
              >
                All
              </Button>
              <Button
                size="sm"
                variant={addressFilter === 'billing' ? 'dark' : 'outline-dark'}
                onClick={() => setAddressFilter('billing')}
              >
                Billing
              </Button>
              <Button
                size="sm"
                variant={addressFilter === 'default' ? 'dark' : 'outline-dark'}
                onClick={() => setAddressFilter('default')}
              >
                Default
              </Button>
            </div>
          </div>

          {filteredAddresses.length === 0 ? (
            <Card>
              <Card.Body className="text-center py-4">
                <h6 className="mb-1">No addresses in this filter</h6>
                <p className="text-muted mb-0">Try switching to another filter.</p>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-3">
              {filteredAddresses.map((address) => (
                <Col md={6} key={address.id}>
                  <Card className={address.is_default ? 'border-primary' : ''}>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h5 className="mb-1">{address.name}</h5>
                          {address.is_default && <Badge bg="primary">Default</Badge>}
                          {address.is_billing && <Badge bg="success" className="ms-2">Billing</Badge>}
                        </div>
                        <div className="d-flex gap-2">
                          <Button variant="outline-secondary" size="sm" onClick={() => openEditModal(address)}>
                            Edit
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDelete(address.id)}>
                            Delete
                          </Button>
                        </div>
                      </div>
                      <div className="fw-semibold">{address.name}</div>
                      <div>{address.contact_isd_code} {address.contact_number}</div>
                      <div>{address.addr_line1}</div>
                      {address.addr_line2 && <div>{address.addr_line2}</div>}
                      <div>{address.city}, {address.state}</div>
                      <div>{address.country} - {address.pincode}</div>
                      {!address.is_default && (
                        <Button variant="link" className="px-0 mt-2" onClick={() => handleSetDefault(address.id)}>
                          Set as default
                        </Button>
                      )}
                      {!address.is_billing && (
                        <Button variant="link" className="px-0 mt-2 d-block" onClick={() => handleSetBilling(address.id)}>
                          Set as billing
                        </Button>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </>
      )}

      <Modal show={showModal} onHide={resetForm} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingAddress ? 'Edit Address' : 'Add Address'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit} noValidate>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control name="name" value={formData.name} onChange={handleChange} maxLength={80} required />
            </Form.Group>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>ISD Code</Form.Label>
                  <Form.Select
                    name="contact_isd_code"
                    value={formData.contact_isd_code}
                    onChange={handleChange}
                    disabled={!canSelectOtherIsd}
                    required
                  >
                    {COUNTRY_ISD_CODES.map((entry) => (
                      <option key={entry.code} value={entry.code}>
                        {entry.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Number</Form.Label>
                  <Form.Control
                    name="contact_number"
                    value={formData.contact_number}
                    onChange={handleChange}
                    inputMode="numeric"
                    maxLength={phoneRule.maxLength}
                    placeholder={`Enter ${phoneLengthHint}`}
                    required
                  />
                  <Form.Text className="text-muted">Required for {formData.contact_isd_code}: {phoneLengthHint}</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Address Line 1</Form.Label>
              <Form.Control name="addr_line1" value={formData.addr_line1} onChange={handleChange} maxLength={100} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Address Line 2</Form.Label>
              <Form.Control name="addr_line2" value={formData.addr_line2} onChange={handleChange} maxLength={100} />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control name="city" value={formData.city} onChange={handleChange} maxLength={40} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>State / UT</Form.Label>
                  <Form.Select name="state" value={formData.state} onChange={handleChange} required>
                    <option value="">Select</option>
                    {stateOptions.map((stateOption) => (
                      <option key={stateOption.id} value={stateOption.name}>
                        {stateOption.name} ({stateOption.region_type})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Control name="country" value="India" disabled readOnly />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Pincode</Form.Label>
                  <Form.Control name="pincode" value={formData.pincode} onChange={handleChange} inputMode="numeric" required />
                </Form.Group>
              </Col>
            </Row>
            <Form.Check
              type="checkbox"
              id="default-address"
              name="is_default"
              label="Mark as default address"
              checked={formData.is_default}
              onChange={handleChange}
            />
            <Form.Check
              type="checkbox"
              id="billing-address"
              name="is_billing"
              label="Mark as billing address"
              checked={formData.is_billing}
              onChange={handleChange}
              className="mt-2"
            />
          </Modal.Body>
          <Modal.Footer className="buyer-address-modal-footer">
            <Button variant="secondary" className="buyer-address-modal-btn" onClick={resetForm}>Cancel</Button>
            <Button type="submit" variant="primary" className="buyer-address-modal-btn" disabled={saving}>{saving ? 'Saving...' : 'Save Address'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={confirmDialog.show} onHide={closeConfirmDialog} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirmDialog.title || 'Confirm Action'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirmDialog.message}</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeConfirmDialog}>Cancel</Button>
          <Button variant="danger" onClick={executeConfirmDialogAction}>
            {confirmDialog.confirmText || 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BuyerAddressManager;
